import type { TimelineSegment } from '../api/flashbackApi/types.ts';
import { resolveStreamPlaybackUrl } from './live-stream.ts';
import {
  playlistSeekFromWallClockMs,
  sortSegmentsChronological,
} from './timeline-playback.ts';

function isPlayableSegment(segment: TimelineSegment): boolean {
  return segment.status === 'complete' || segment.status === 'in-progress';
}

export type ResolvedSegmentPlayback = {
  segment: TimelineSegment;
  /** Seconds from the start of this segment's media file. */
  offsetSeconds: number;
};

/**
 * Maps a wall-clock instant to the timeline segment that contains it and the
 * seek offset inside that segment's MP4 (not a virtual multi-segment playlist offset).
 */
export function resolvePlaybackAtWallClock(
  segments: TimelineSegment[],
  wallMs: number,
): ResolvedSegmentPlayback | null {
  const { segmentId } = playlistSeekFromWallClockMs(segments, wallMs);
  if (!segmentId) return null;
  const segment = segments.find((s) => s.id === segmentId);
  if (!segment || !isPlayableSegment(segment)) return null;

  const startMs = new Date(segment.startTime).getTime();
  const endMs = new Date(segment.endTime).getTime();
  const clampedMs = Math.max(startMs, Math.min(wallMs, endMs));
  const offsetSeconds = Math.max(0, (clampedMs - startMs) / 1000);

  return { segment, offsetSeconds };
}

/** Wall-clock ms from the active segment file position. */
export function wallClockMsFromSegmentPlayback(
  segment: TimelineSegment,
  videoCurrentTimeSeconds: number,
): number {
  const startMs = new Date(segment.startTime).getTime();
  return startMs + Math.max(0, videoCurrentTimeSeconds) * 1000;
}

/** Next chronological playable segment after `segmentId`, if any. */
export function nextPlayableSegment(
  segments: TimelineSegment[],
  segmentId: string,
): TimelineSegment | null {
  const ordered = sortSegmentsChronological(segments).filter(isPlayableSegment);
  const idx = ordered.findIndex((s) => s.id === segmentId);
  if (idx < 0 || idx >= ordered.length - 1) return null;
  return ordered[idx + 1] ?? null;
}

export function resolveSegmentMp4Url(playbackUrl: string): string {
  return resolveStreamPlaybackUrl(playbackUrl);
}

const END_ROLLOVER_EPSILON_S = 0.35;

/** True when playback is near the end of the segment and should advance. */
export function shouldAdvanceToNextSegment(
  segment: TimelineSegment,
  videoCurrentTimeSeconds: number,
): boolean {
  const nearFileEnd =
    Number.isFinite(videoCurrentTimeSeconds) &&
    videoCurrentTimeSeconds >= segment.durationSeconds - END_ROLLOVER_EPSILON_S;
  return nearFileEnd;
}

export type LoadRecordingMp4Opts = {
  signal?: AbortSignal;
  onWaiting?: () => void;
  onCanPlay?: () => void;
  /** Abort load if metadata is not ready within this time (default 90s). */
  timeoutMs?: number;
};

const DEFAULT_SEGMENT_LOAD_TIMEOUT_MS = 90_000;

/**
 * Loads an authenticated segment MP4 URL and seeks to `offsetSeconds`.
 * Reuses the same element when the URL is unchanged (seek only).
 */
export function loadRecordingMp4(
  video: HTMLVideoElement,
  playbackUrl: string,
  offsetSeconds: number,
  opts?: LoadRecordingMp4Opts,
): Promise<void> {
  const url = resolveSegmentMp4Url(playbackUrl);
  if (!url) {
    return Promise.reject(new Error('No recording playback URL'));
  }

  const signal = opts?.signal;
  if (signal?.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'));
  }

  const targetOffset = Math.max(0, offsetSeconds);

  const onWaiting = (): void => opts?.onWaiting?.();
  const onCanPlay = (): void => opts?.onCanPlay?.();
  video.addEventListener('waiting', onWaiting);
  video.addEventListener('canplay', onCanPlay);

  const cleanupListeners = (): void => {
    video.removeEventListener('waiting', onWaiting);
    video.removeEventListener('canplay', onCanPlay);
  };

  const seekWhenReady = (): Promise<void> =>
    new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      const timeoutMs = opts?.timeoutMs ?? DEFAULT_SEGMENT_LOAD_TIMEOUT_MS;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const cleanup = (): void => {
        if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        video.removeEventListener('loadedmetadata', onMeta);
        video.removeEventListener('error', onErr);
        signal?.removeEventListener('abort', onAbort);
      };

      const applySeek = (): void => {
        if (signal?.aborted) {
          cleanup();
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }
        const duration =
          Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
        const seekTo =
          duration != null
            ? Math.min(targetOffset, Math.max(0, duration - 0.05))
            : targetOffset;
        video.currentTime = seekTo;
        void video.play().catch(() => undefined);
        cleanup();
        resolve();
      };

      const onMeta = (): void => {
        applySeek();
      };
      const onErr = (): void => {
        cleanup();
        reject(new Error('Failed to load recording segment'));
      };
      const onAbort = (): void => {
        cleanup();
        reject(new DOMException('Aborted', 'AbortError'));
      };

      timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error('Recording segment took too long to load'));
      }, timeoutMs);

      signal?.addEventListener('abort', onAbort, { once: true });

      if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
        applySeek();
        return;
      }

      video.addEventListener('loadedmetadata', onMeta, { once: true });
      video.addEventListener('error', onErr, { once: true });
    });

  const sameSrc = isSameMediaUrl(video.currentSrc, url) || isSameMediaUrl(video.src, url);

  if (sameSrc) {
    return seekWhenReady().finally(cleanupListeners);
  }

  video.pause();
  video.src = url;
  video.load();

  return seekWhenReady().finally(cleanupListeners);
}

/**
 * Compares two media URLs ignoring origin / encoding differences so an already-loaded
 * source is not reloaded just because the browser normalized `video.currentSrc`.
 */
function isSameMediaUrl(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  try {
    const ua = new URL(a, window.location.href);
    const ub = new URL(b, window.location.href);
    return ua.pathname === ub.pathname && ua.search === ub.search;
  } catch {
    return false;
  }
}
